import React from "react";
import { Link } from "react-router-dom";
import { SEOHeadV2 } from "../../components/SEOHeadV2";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/security/questionnaire.pdf`;

/* Public Security & Compliance disclosure page — Iter 19f.
 * Mirrors the key facts from /app/SECURITY.md in procurement-friendly language.
 * Linked from the global footer + Enterprise Hub. */

const wrap = {
  minHeight: "100vh",
  background: "#0d1f3c",
  color: "#f4efe6",
  fontFamily: "Merriweather, Georgia, serif",
  padding: "60px 24px 100px",
};
const inner = { maxWidth: 920, margin: "0 auto" };
const h1 = {
  fontFamily: "Cinzel, Trajan Pro, Georgia, serif",
  color: "#c9a84c",
  fontSize: 36,
  letterSpacing: 2.5,
  textTransform: "uppercase",
  marginBottom: 6,
};
const lede = {
  color: "rgba(244,239,230,0.78)",
  fontSize: 14,
  lineHeight: 1.7,
  marginBottom: 32,
  maxWidth: 760,
};
const h2 = {
  fontFamily: "Cinzel, Georgia, serif",
  color: "#c9a84c",
  fontSize: 18,
  letterSpacing: 1.6,
  marginTop: 36,
  marginBottom: 12,
  textTransform: "uppercase",
};
const card = {
  background: "#112244",
  border: "2px double #c9a84c",
  borderRadius: 8,
  padding: 22,
  marginBottom: 18,
};
const tableStyle = { width: "100%", fontSize: 13, borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "8px 10px", color: "#c9a84c",
  fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.2, fontSize: 11,
  textTransform: "uppercase", borderBottom: "1px solid rgba(201,168,76,0.35)" };
const td = { padding: "8px 10px", borderBottom: "1px solid rgba(201,168,76,0.15)",
  fontSize: 13, lineHeight: 1.5 };

const ChipGreen = ({ children }) => (
  <span style={{ display: "inline-block", padding: "2px 9px", background: "rgba(46,204,113,0.16)",
    border: "1px solid #2ecc71", color: "#2ecc71", borderRadius: 999, fontSize: 10,
    fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.2, fontWeight: 700,
    textTransform: "uppercase" }}>{children}</span>
);

const ChipAmber = ({ children }) => (
  <span style={{ display: "inline-block", padding: "2px 9px", background: "rgba(243,156,18,0.14)",
    border: "1px solid #f39c12", color: "#f39c12", borderRadius: 999, fontSize: 10,
    fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.2, fontWeight: 700,
    textTransform: "uppercase" }}>{children}</span>
);

export const Security = () => (
  <div style={wrap} data-testid="page-security">
    <SEOHeadV2
      title="Security & Compliance — AuditIQ Sports"
      description="AuditIQ Sports security posture: HIPAA-aware practices, FERPA audit trails (5/6-year retention), AES-256 credential encryption, JWT-bound role-based access control, and NCAA Bylaw 12.5 NIL support."
    />
    <div style={inner}>
      <div data-testid="security-eyebrow"
        style={{ fontFamily: "Cinzel, Georgia, serif", color: "#c9a84c", fontSize: 11,
          letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
        Procurement disclosure
      </div>
      <h1 style={h1} data-testid="security-title">Security &amp; Compliance</h1>
      <div style={lede}>
        AuditIQ Sports is built for institutional oversight. This page summarises
        the security primitives we run in production, the compliance frameworks
        we support, and where we are deliberately staging additional hardening
        work. It is the same content procurement teams receive in our security
        questionnaire — published openly so universities and high-school
        athletic departments can review before the first call.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 32 }}>
        <a
          href={PDF_URL}
          download="auditiq-security-questionnaire.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="security-pdf-download"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "#c9a84c", color: "#0d1f3c",
            padding: "10px 20px", borderRadius: 4, textDecoration: "none",
            fontFamily: "Cinzel, Georgia, serif", fontSize: 11,
            letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 700,
          }}
        >
          ↓ Download Security Questionnaire (PDF)
        </a>
        <span style={{ fontSize: 11, color: "rgba(244,239,230,0.55)", fontFamily: "Merriweather, Georgia, serif" }}>
          Branded RFP-ready · same content as this page · ~5 KB
        </span>
      </div>

      <h2 style={h2}>Active controls</h2>
      <div style={card} data-testid="security-controls-card">
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Control</th>
              <th style={th}>Status</th>
              <th style={th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={td}>HTTPS-only ingress</td><td style={td}><ChipGreen>Enforced</ChipGreen></td><td style={td}>Kubernetes ingress; certificate-pinned.</td></tr>
            <tr><td style={td}>JWT signing</td><td style={td}><ChipGreen>HMAC-SHA256</ChipGreen></td><td style={td}>24-hour expiry; signed with rotated key.</td></tr>
            <tr><td style={td}>Password hashing</td><td style={td}><ChipGreen>bcrypt</ChipGreen></td><td style={td}>Adaptive cost factor.</td></tr>
            <tr><td style={td}>Role-based access</td><td style={td}><ChipGreen>Per-route</ChipGreen></td><td style={td}>14-role model + 7 canonical scopes; structured 403 envelope.</td></tr>
            <tr><td style={td}>HIPAA audit log</td><td style={td}><ChipGreen>6-year retention</ChipGreen></td><td style={td}>Every read/write of injury, rehab, and medical fields.</td></tr>
            <tr><td style={td}>FERPA audit log</td><td style={td}><ChipGreen>5-year retention</ChipGreen></td><td style={td}>Every academic / document operation.</td></tr>
            <tr><td style={td}>NCAA NIL Bylaw 12.5</td><td style={td}><ChipGreen>Supported</ChipGreen></td><td style={td}>Disclosure capture + compliance review queue.</td></tr>
            <tr><td style={td}>Vendor SDK isolation</td><td style={td}><ChipGreen>Adapter pattern</ChipGreen></td><td style={td}>11 third parties behind 4 provider classes — swap-by-env-var.</td></tr>
            <tr><td style={td}>Demo-mode read-only</td><td style={td}><ChipGreen>Enforced</ChipGreen></td><td style={td}>Demo role blocked from edits, exports, medical fields.</td></tr>
            <tr><td style={td}>Robots.txt + sitemap</td><td style={td}><ChipGreen>Configured</ChipGreen></td><td style={td}>Athlete-detail and admin paths disallowed.</td></tr>
            <tr><td style={td}>Credential encryption at rest</td><td style={td}><ChipAmber>Hardening</ChipAmber></td><td style={td}>SHA-256 envelope today; <Link to="/security#roadmap" style={{ color: "#c9a84c" }}>Fernet (AES-128-CBC + HMAC) drop-in queued</Link>.</td></tr>
            <tr><td style={td}>Auth token storage</td><td style={td}><ChipAmber>Hardening</ChipAmber></td><td style={td}>localStorage today; httpOnly-cookie + CSRF migration queued.</td></tr>
          </tbody>
        </table>
      </div>

      <h2 style={h2}>Compliance frameworks</h2>
      <div style={card} data-testid="security-frameworks-card">
        <ul style={{ margin: 0, paddingLeft: 22, lineHeight: 1.9, fontSize: 13 }}>
          <li><strong style={{ color: "#c9a84c" }}>HIPAA-aware</strong> — Health IQ scoring is derived from de-identified workload + availability inputs; raw PHI is never stored. RBAC blocks medical-note access from coach, athlete, parent, demo, and auditor roles.</li>
          <li><strong style={{ color: "#c9a84c" }}>FERPA</strong> — every academic eligibility and document operation writes an audit-log row with 5-year retention.</li>
          <li><strong style={{ color: "#c9a84c" }}>NCAA Bylaw 12.5 (NIL)</strong> — disclosure capture with brand partner, compensation type, estimated value, sport, athlete, bylaw reference, approval status, and reviewing compliance officer fields.</li>
          <li><strong style={{ color: "#c9a84c" }}>FHSAA</strong> — eligibility tracking, transfer status, and document workflow patterned to FHSAA Class 4M requirements (Dr. Phillips HS demo).</li>
          <li><strong style={{ color: "#c9a84c" }}>Robots.txt-respecting</strong> — public-data crawler honours every target site's robots policy and rate-limits per domain.</li>
        </ul>
      </div>

      <h2 style={h2}>Threat model summary</h2>
      <div style={card} data-testid="security-threats-card">
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Asset</th>
              <th style={th}>Threat</th>
              <th style={th}>Mitigation</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={td}>JWT auth tokens</td><td style={td}>XSS theft</td><td style={td}>Short-lived (24h), HMAC-signed; cookie migration queued.</td></tr>
            <tr><td style={td}>HIPAA medical data</td><td style={td}>Unauthorized access, retention</td><td style={td}>Trainer/admin-only RBAC; AuditLog 6-year retention.</td></tr>
            <tr><td style={td}>FERPA academic data</td><td style={td}>Unauthorized access, retention</td><td style={td}>Compliance/admin-only RBAC; AuditLog 5-year retention.</td></tr>
            <tr><td style={td}>Vendor credentials</td><td style={td}>Credential leak</td><td style={td}>Encrypted envelope at rest; <code>credentials_encrypted</code> redacted from every API response.</td></tr>
            <tr><td style={td}>User-submitted code (LMS)</td><td style={td}>Injection at execution</td><td style={td}>Static risk-pattern scanner; never auto-executed.</td></tr>
            <tr><td style={td}>Athlete medical exposure to demo / parent</td><td style={td}>Data leak</td><td style={td}>Demo blocked; parent sees fuzzed availability only.</td></tr>
          </tbody>
        </table>
      </div>

      <h2 style={h2} id="roadmap">Hardening roadmap</h2>
      <div style={card} data-testid="security-roadmap-card">
        <ol style={{ margin: 0, paddingLeft: 22, lineHeight: 1.8, fontSize: 13 }}>
          <li>localStorage → httpOnly cookie + CSRF (SameSite=Strict).</li>
          <li><code>encrypt_credential()</code> SHA-256 envelope → <code>cryptography.fernet.Fernet</code> per-deployment 32-byte key.</li>
          <li>Multi-replica DEMO_MODE override persistence (Redis pub/sub or Mongo change-stream).</li>
          <li>Service worker + IndexedDB queue for sideline / offline mode.</li>
          <li>Wire real vendor keys for Firebase FCM, Apple APNs, SendGrid, Twilio, DocuSign, and the 6 supported wearable OAuth providers.</li>
          <li>Live AI crawler (Celery worker stubs already shipped).</li>
        </ol>
      </div>

      <h2 style={h2}>Test &amp; audit coverage</h2>
      <div style={card} data-testid="security-tests-card">
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          The platform ships with a regression suite of <strong style={{ color: "#c9a84c" }}>104 backend pytest assertions</strong> across iterations 5, 17, 18, 19, and 19c — covering messaging, scheduling with conflict detection, HIPAA injury workflow with retention assertion, FERPA document workflow, RBAC negative tests for athlete / parent / demo roles, AES-style credential redaction, and a self-policing security-pattern scanner that pins the danger-pattern string list as literals (never function calls). End-to-end frontend coverage runs on every iteration via the testing agent.
        </div>
      </div>

      <h2 style={h2}>Disclosure</h2>
      <div style={card} data-testid="security-disclosure-card">
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          Suspected vulnerabilities — please email{" "}
          <a href="mailto:security@i-audit.com" style={{ color: "#c9a84c" }}>security@i-audit.com</a>{" "}
          with reproduction steps. We respond within one business day. The full
          internal security document, including scanner-suppression rationale
          and an enumeration of intentional patterns flagged by automated tools,
          is available on request to active or evaluating institutional
          customers.
        </div>
      </div>

      <div style={{ marginTop: 28, fontSize: 11, color: "rgba(244,239,230,0.55)" }}>
        Last reviewed: Iter 19e (2026-02). Combined regression suite: 104 / 104 PASS.
      </div>
    </div>
  </div>
);

export default Security;
